/**
 * App.tsx — Roteamento principal do Órbita.
 *
 * Dois mundos convivendo:
 *   1. Site institucional Nectaris (público, sem autenticação) — /
 *   2. App autenticado do Órbita — /app/* protegido por AuthGuard + RouteGuard
 *
 * Rotas públicas do site: /, /o-grupo, /iniciativas, /contato
 * Login: /entrar (público) — também acessível via /orbita/entrar (subpath Firebase)
 * App autenticado: /app/* — também via /orbita/app/* (ADR-009 subpath hosting)
 *
 * Providers aninhados (ADR-003, NAVEGACAO.md §7):
 *   AuthProvider → TenantProvider → FacilityProvider → Router → Routes
 */

import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router";

// --- Providers ---
import { AuthProvider } from "@/features/auth/AuthContext";
import { TenantProvider } from "@/features/shell/TenantContext";
import { FacilityProvider } from "@/features/shell/FacilityContext";
import { MenuConfigProvider } from "@/features/configuracoes/menu-config-context";

// --- Auth ---
import { LoginPage } from "@/features/auth/LoginPage";
import { RouteGuard, AppGuard } from "@/features/auth/RouteGuard";
import { FluxoCaixaLayout } from "@/features/fluxo-caixa/FluxoCaixaLayout";

// --- Shell ---
import { AppShell } from "@/features/shell/AppShell";
import { VisaoExecutivaPage } from "@/features/shell/pages/VisaoExecutivaPage";

// --- Site institucional (fase 1) ---
import { SiteLayout } from "@/features/site/SiteLayout";
import { HomePage } from "@/features/site/pages/HomePage";
import { OGrupoPage } from "@/features/site/pages/OGrupoPage";
import { IniciativasPage } from "@/features/site/pages/IniciativasPage";
import { ContatoPage } from "@/features/site/pages/ContatoPage";
import { NotFoundPage } from "@/features/site/pages/NotFoundPage";

// ---------------------------------------------------------------------------
// Lazy loading dos módulos do app autenticado (NAVEGACAO.md §0 — obrigatório)
// Cada módulo = chunk separado; carregado apenas quando acessado.
// ---------------------------------------------------------------------------

// Módulos financeiros
const FluxoCaixaPage = lazy(() =>
  import("@/features/fluxo-caixa/FluxoCaixaPage").then((m) => ({
    default: m.FluxoCaixaPage,
  })),
);
const FluxoMatrizPage = lazy(() =>
  import("@/features/fluxo-matriz/FluxoMatrizPage").then((m) => ({
    default: m.FluxoMatrizPage,
  })),
);
const ContasReceberPage = lazy(() =>
  import("@/features/contas/ContasReceberPage").then((m) => ({
    default: m.ContasReceberPage,
  })),
);
const ContasPagarPage = lazy(() =>
  import("@/features/contas/ContasPagarPage").then((m) => ({
    default: m.ContasPagarPage,
  })),
);
const RecebimentosPage = lazy(() =>
  import("@/features/recebimentos/RecebimentosPage").then((m) => ({
    default: m.RecebimentosPage,
  })),
);
const ImportacaoPagamentosPage = lazy(() =>
  import("@/features/importacao-pagamentos/ImportacaoPagamentosPage").then((m) => ({
    default: m.ImportacaoPagamentosPage,
  })),
);
const ComprasPage = lazy(() =>
  import("@/features/shell/pages/ModulePlaceholderPage").then((m) => ({
    default: () => <m.ModulePlaceholderPage moduleName="Compras" />,
  })),
);
const EstoquePage = lazy(() =>
  import("@/features/shell/pages/ModulePlaceholderPage").then((m) => ({
    default: () => <m.ModulePlaceholderPage moduleName="Estoque" />,
  })),
);
const CustosPage = lazy(() =>
  import("@/features/custos/CustosPage").then((m) => ({
    default: m.CustosPage,
  })),
);

// Custeio — FASE 3.9 (motor de rateio configurável + margem por procedimento)
const CusteioPage = lazy(() =>
  import("@/features/custeio/CusteioPage").then((m) => ({
    default: m.CusteioPage,
  })),
);
const RateiosPage = lazy(() =>
  import("@/features/rateios/RateiosPage").then((m) => ({
    default: m.RateiosPage,
  })),
);
const PrecificacaoPage = lazy(() =>
  import("@/features/precificacao/PrecificacaoPage").then((m) => ({
    default: m.PrecificacaoPage,
  })),
);

// Cenários de fluxo de caixa (comparação de overlays; UX-CENARIOS)
const CenariosPage = lazy(() =>
  import("@/features/cenarios/CenariosPage").then((m) => ({
    default: m.CenariosPage,
  })),
);

// Necessidade de caixa (leitura executiva de capital de giro; UX-NECESSIDADE-DE-CAIXA)
const NecessidadeCaixaPage = lazy(() =>
  import("@/features/necessidade-caixa/NecessidadeCaixaPage").then((m) => ({
    default: m.NecessidadeCaixaPage,
  })),
);

// Módulos comerciais
const VendasPage = lazy(() =>
  import("@/features/vendas").then((m) => ({ default: m.VendasPage })),
);
const ProcedimentosPage = lazy(() =>
  import("@/features/procedimentos").then((m) => ({ default: m.ProcedimentosPage })),
);

// Cadastros: Fornecedores (master prompt secao 30)
const FornecedoresPage = lazy(() =>
  import("@/features/fornecedores").then((m) => ({ default: m.FornecedoresPage })),
);

// Cadastros: Fontes de Receita (origens de faturamento: convenio, particular, SUS, outros)
const FontesReceitaPage = lazy(() =>
  import("@/features/fontes-receita").then((m) => ({ default: m.FontesReceitaPage })),
);

// Cadastros: Estrutura Fisica (setores e espacos por unidade)
const EstruturaFisicaPage = lazy(() =>
  import("@/features/estrutura-fisica").then((m) => ({ default: m.EstruturaFisicaPage })),
);

// Cadastros: Fontes de Dados (registro das planilhas + importacao de XLSX)
const FontesDadosPage = lazy(() =>
  import("@/features/fontes-dados/FontesDadosPage").then((m) => ({ default: m.FontesDadosPage })),
);

// Assistencial — delegado ao agente assistential-ux
const AssistencialPage = lazy(() =>
  import("@/features/shell/pages/ModulePlaceholderPage").then((m) => ({
    default: () => <m.ModulePlaceholderPage moduleName="Explorador Assistencial" />,
  })),
);

// Ingestão XLSX (FASE 3.4)
const IngestaoPage = lazy(() =>
  import("@/features/ingestao/IngestaoPage").then((m) => ({
    default: m.IngestaoPage,
  })),
);

// Configuração
const EstruturaPage = lazy(() =>
  import("@/features/estrutura/EstruturaPage").then((m) => ({
    default: m.EstruturaPage,
  })),
);
const IntegracoesPage = lazy(() =>
  import("@/features/integracoes/IntegracoesPage").then((m) => ({
    default: m.IntegracoesPage,
  })),
);
const UsuariosPage = lazy(() =>
  import("@/features/shell/pages/ModulePlaceholderPage").then((m) => ({
    default: () => <m.ModulePlaceholderPage moduleName="Usuários" />,
  })),
);
const ConfiguracoesPage = lazy(() =>
  import("@/features/configuracoes/ConfiguracoesPage").then((m) => ({
    default: m.ConfiguracoesPage,
  })),
);

// ---------------------------------------------------------------------------
// Spinner de carregamento de página (lazy Suspense fallback)
// ---------------------------------------------------------------------------
function PageSpinner() {
  return (
    <div
      role="status"
      aria-label="Carregando módulo…"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px",
      }}
    >
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        aria-hidden="true"
        style={{ animation: "spin 1s linear infinite", color: "var(--color-accent, #9A6F1E)" }}
      >
        <circle
          cx="14"
          cy="14"
          r="10"
          stroke="currentColor"
          strokeWidth="2.5"
          fill="none"
          strokeDasharray="44"
          strokeDashoffset="22"
        />
      </svg>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App principal
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <FacilityProvider>
          {/* MenuConfigProvider: um único onSnapshot da config de menu por tenant,
              compartilhado por sidebar e tela de Configurações (CONFIG-MENU §3.4). */}
          <MenuConfigProvider>
          <Routes>
            {/* ================================================================
                SITE INSTITUCIONAL — público, sem autenticação
                Rotas do Nectaris (fase 1): /, /o-grupo, /iniciativas, /contato
                ================================================================ */}
            <Route element={<SiteLayout />}>
              <Route index element={<HomePage />} />
              <Route path="o-grupo" element={<OGrupoPage />} />
              <Route path="iniciativas" element={<IniciativasPage />} />
              <Route path="contato" element={<ContatoPage />} />
            </Route>

            {/* ================================================================
                LOGIN — público
                Rota: /entrar
                ================================================================ */}
            <Route path="entrar" element={<LoginPage />} />

            {/* ================================================================
                APP AUTENTICADO — protegido por AppGuard + RouteGuard
                Shell: Sidebar + Topbar + Outlet
                Todas as rotas sob /app/* exigem autenticação + claim orbita
                ================================================================ */}
            <Route
              path="app"
              element={
                <AppGuard>
                  <AppShell />
                </AppGuard>
              }
            >
              {/* Visão Executiva — rota índice do app (/app) */}
              <Route
                index
                element={
                  <RouteGuard moduleId="visao-executiva" minRole="viewer">
                    <VisaoExecutivaPage />
                  </RouteGuard>
                }
              />

              {/* Financeiro — família de caixa consolidada em ABAS (FluxoCaixaLayout) */}
              <Route element={<FluxoCaixaLayout />}>
              <Route
                path="financeiro/fluxo-caixa"
                element={
                  <RouteGuard moduleId="fluxo-caixa" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <FluxoCaixaPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/fluxo-matriz"
                element={
                  <RouteGuard moduleId="fluxo-matriz" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <FluxoMatrizPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/cenarios"
                element={
                  <RouteGuard moduleId="cenarios" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <CenariosPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/necessidade-de-caixa"
                element={
                  <RouteGuard moduleId="necessidade-de-caixa" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <NecessidadeCaixaPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/contas-a-receber"
                element={
                  <RouteGuard moduleId="contas-a-receber" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <ContasReceberPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/contas-a-pagar"
                element={
                  <RouteGuard moduleId="contas-a-pagar" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <ContasPagarPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/recebimentos"
                element={
                  <RouteGuard moduleId="recebimentos" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <RecebimentosPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/importacao-pagamentos"
                element={
                  <RouteGuard moduleId="importacao-pagamentos" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <ImportacaoPagamentosPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              </Route>
              <Route
                path="financeiro/compras"
                element={
                  <RouteGuard moduleId="compras" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <ComprasPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/estoque"
                element={
                  <RouteGuard moduleId="estoque" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <EstoquePage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/custos"
                element={
                  <RouteGuard moduleId="custos" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <CustosPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/rateios"
                element={
                  <RouteGuard moduleId="rateios" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <RateiosPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              {/* Custeio / Margem por Procedimento — FASE 3.9 */}
              <Route
                path="financeiro/custeio"
                element={
                  <RouteGuard moduleId="custos" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <CusteioPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="financeiro/precificacao"
                element={
                  <RouteGuard moduleId="precificacao" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <PrecificacaoPage />
                    </Suspense>
                  </RouteGuard>
                }
              />

              {/* Comercial */}
              <Route
                path="comercial/vendas"
                element={
                  <RouteGuard moduleId="vendas" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <VendasPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="comercial/procedimentos"
                element={
                  <RouteGuard moduleId="procedimentos" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <ProcedimentosPage />
                    </Suspense>
                  </RouteGuard>
                }
              />

              {/* Cadastros */}
              <Route
                path="cadastros/fornecedores"
                element={
                  <RouteGuard moduleId="fornecedores" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <FornecedoresPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="cadastros/fontes-receita"
                element={
                  <RouteGuard moduleId="fontes-receita" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <FontesReceitaPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="cadastros/estrutura-fisica"
                element={
                  <RouteGuard moduleId="estrutura-fisica" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <EstruturaFisicaPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="cadastros/fontes-dados"
                element={
                  <RouteGuard moduleId="fontes-dados" minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <FontesDadosPage />
                    </Suspense>
                  </RouteGuard>
                }
              />

              {/* Assistencial */}
              <Route
                path="assistencial"
                element={
                  <RouteGuard moduleId="assistencial" minRole="viewer">
                    <Suspense fallback={<PageSpinner />}>
                      <AssistencialPage />
                    </Suspense>
                  </RouteGuard>
                }
              />

              {/* Ingestão XLSX — FASE 3.4 */}
              <Route
                path="ingestao"
                element={
                  <RouteGuard minRole="financeiro">
                    <Suspense fallback={<PageSpinner />}>
                      <IngestaoPage />
                    </Suspense>
                  </RouteGuard>
                }
              />

              {/* Configuração */}
              <Route
                path="estrutura"
                element={
                  <RouteGuard moduleId="estrutura" minRole="viewer">
                    <Suspense fallback={<PageSpinner />}>
                      <EstruturaPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="integracoes"
                element={
                  <RouteGuard moduleId="integracoes" minRole="admin">
                    <Suspense fallback={<PageSpinner />}>
                      <IntegracoesPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="usuarios"
                element={
                  <RouteGuard moduleId="usuarios" minRole="admin">
                    <Suspense fallback={<PageSpinner />}>
                      <UsuariosPage />
                    </Suspense>
                  </RouteGuard>
                }
              />
              <Route
                path="configuracoes"
                element={
                  <RouteGuard moduleId="configuracoes" minRole="admin">
                    <Suspense fallback={<PageSpinner />}>
                      <ConfiguracoesPage />
                    </Suspense>
                  </RouteGuard>
                }
              />

              {/* Fallback dentro do app autenticado */}
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Route>

            {/* ================================================================
                SUBPATH /orbita — suporte ao Firebase Hosting /orbita/*
                Aliases para que links diretos /orbita/entrar e /orbita/app/*
                funcionem no SPA (ADR-009 §Decisão 2).
                ================================================================ */}
            <Route path="orbita">
              <Route path="entrar" element={<LoginPage />} />
              <Route
                path="app"
                element={
                  <AppGuard>
                    <AppShell />
                  </AppGuard>
                }
              >
                <Route index element={<Navigate to="/app" replace />} />
                <Route path="*" element={<Navigate to="/app" replace />} />
              </Route>
              {/* Redireciona /orbita e /orbita/* sem match para o site */}
              <Route index element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* ================================================================
                FALLBACK GLOBAL — redireciona 404 do site para NotFound
                ================================================================ */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </MenuConfigProvider>
        </FacilityProvider>
      </TenantProvider>
    </AuthProvider>
  );
}
